/** Pure helpers for OrderContactsField (kept out of the .tsx for react-refresh). */
import type {
  NotificationContact,
  OrderContactCandidate,
} from '../../types/orderNotifications';

export function candidateToContact(c: OrderContactCandidate): NotificationContact {
  return {
    name: c.name,
    email: c.email.toLowerCase(),
    source: c.source,
    contact_id: c.contact_id,
    confirmation: c.notify_confirmation ?? true,
    invoice: c.notify_invoice ?? true,
  };
}

/**
 * Snapshots saved before the per-kind flags existed have no `confirmation` /
 * `invoice` keys; they meant "both". Normalise at the boundary.
 */
export function normalizeContacts(contacts: Partial<NotificationContact>[] | null | undefined): NotificationContact[] {
  return (contacts ?? []).map((c) => ({
    name: c.name ?? '',
    email: (c.email ?? '').toLowerCase(),
    source: c.source ?? 'manual',
    contact_id: c.contact_id ?? null,
    confirmation: c.confirmation ?? true,
    invoice: c.invoice ?? true,
  }));
}

/** "(invoice only)" / "(confirmation only)" suffix for a recipient, "" for both. */
export function kindSuffix(c: Pick<NotificationContact, 'confirmation' | 'invoice'>): string {
  if (c.confirmation && c.invoice) return '';
  if (c.confirmation) return ' (confirmation only)';
  if (c.invoice) return ' (invoice only)';
  return ' (no emails)';
}

/** Filter + split candidates into (suggested, others), excluding already-picked emails. */
export function filterCandidates(
  candidates: OrderContactCandidate[],
  picked: NotificationContact[],
  query: string,
): { suggested: OrderContactCandidate[]; others: OrderContactCandidate[] } {
  const pickedEmails = new Set(picked.map((p) => p.email.toLowerCase()));
  const q = query.trim().toLowerCase();
  const visible = candidates.filter((c) => {
    if (pickedEmails.has(c.email.toLowerCase())) return false;
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  });
  return {
    suggested: visible.filter((c) => c.suggested),
    others: visible.filter((c) => !c.suggested),
  };
}

/** Axios-style error → user-facing message (FastAPI `detail` string or {message}). */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const msg = (detail as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return fallback;
}

/** The 409 duplicate-contact payload carries the existing row id. */
export function existingContactIdFromError(err: unknown): string | null {
  const detail = (err as { response?: { data?: { detail?: { existing_contact_id?: unknown } } } })
    ?.response?.data?.detail;
  const id = detail && typeof detail === 'object' ? detail.existing_contact_id : null;
  return typeof id === 'string' ? id : null;
}

/** One-line summary for the compact row: names, "+N more", or the empty state. */
export function summarizeContacts(contacts: NotificationContact[], max = 3): string {
  if (contacts.length === 0) return 'No buyer picked';
  const names = contacts.map((c) => `${c.name || c.email}${kindSuffix(c)}`);
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} +${names.length - max} more`;
}

/** Fixed sales-site recipients for the compact row, e.g. "wcconfirmpo@… (sales site)". */
export function summarizeFixed(fixed: { email: string; name: string }[], max = 2): string {
  if (fixed.length === 0) return '';
  const names = fixed.map((f) => f.email);
  const shown = names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} +${names.length - max}`;
  return `${shown} (sales site)`;
}

/**
 * Is the search-box text an email address (or the start of one) rather than a
 * name? Anything with an "@" and no spaces is treated as an address so the
 * New-contact form pre-fills Email, not Name (kanban 9479e4de).
 */
export function looksLikeEmail(text: string): boolean {
  const t = text.trim();
  return t.includes('@') && !/\s/.test(t);
}
